import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { DcrApproveSearch } from '../types/dcrApprove.types';
import { getQueueDefaultPeriod } from '@/features/billing/utils/period';
import { toDcrApproveFilterSearch, toDcrApproveFilterValues } from '../utils/dcrApproveFilters';

const route = getRouteApi('/_auth/billing/dcr-approve');

const normalizeFilterValues = (values: Record<string, string>) =>
  toDcrApproveFilterValues(toDcrApproveFilterSearch(values));

export function useDcrApproveFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toDcrApproveFilterValues(search), [search]);
  const defaultValues = useMemo(() => getQueueDefaultPeriod(), []);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toDcrApproveFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): DcrApproveSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultValues.dateFrom,
      dateTo: search.dateTo ?? defaultValues.dateTo,
    }),
    [search, defaultValues],
  );

  const zmSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...zmSearch, params };
}
